// ---------------------------------------------------------------------------
// Spanish dictionary — source of the text keys (`TextKey`, derived from this
// file via `keyof typeof es`). Every new language (see en.ts) has to
// translate exactly these same keys — a missing one is a TypeScript type
// error, not a silent blank text in production.
//
// Key convention: "component.descriptiveName", in lowerCamelCase.
// "comun.*" is for generic text shared across several components (Close,
// Save, Cancel...) — to keep every component from reinventing its own key
// for the same thing.
//
// Values can carry "{name}"-style placeholders for interpolation (see t() in
// context.tsx) — e.g. to include a project's name in a message.
// ---------------------------------------------------------------------------

export const es = {
  // --- Comunes (compartidos por varios componentes) ---
  "common.close": "Cerrar",
  "common.date": "Fecha",
  "common.save": "Guardar",
  "common.cancel": "Cancelar",
  "common.emptyName": "El nombre no puede estar vacío.",
  "common.language": "Idioma",
  "common.apply": "Aplicar",
  "common.choose": "— elegir —",
  "common.name": "Nombre",
  "common.create": "Crear",
  "common.unassigned": "— sin asignar —",
  "common.unassignedMissingZone": "sin asignar (falta zona)",
  "common.series": "Series",
  "common.back": "Atrás",
  "common.next": "Siguiente",
  "common.finish": "Finalizar",
  "common.add": "Añadir",

  // --- TopBar.tsx ---
  "topbar.closeProject": "Cerrar proyecto",
  "topbar.moreOptions": "Más opciones del proyecto",
  "topbar.exampleUnsavedWarning":
    'Este es un ejemplo, no un proyecto guardado — usa "Guardar copia como…" primero.',
  "topbar.renameProject": "Renombrar proyecto…",
  "topbar.saveCopyAsButton": "Guardar copia como…",
  "topbar.exportCopy": "Exportar copia…",
  "topbar.updateFromCsv": "Actualizar desde CSVs…",
  "topbar.deleteThisProject": "Eliminar este proyecto",
  "topbar.saveAsDialogTitle": "Guardar copia como",
  "topbar.copyNameSuffix": "{name} (copia)",
  "topbar.renameDialogTitle": "Renombrar proyecto",
  "topbar.deleteDialogTitle": "Eliminar proyecto",
  "topbar.deleteProjectMessage":
    'Se eliminará "{name}" de este navegador de forma permanente — no se puede deshacer. Si lo necesitas, puedes exportar una copia antes de eliminarlo.',
  "topbar.help": "Ayuda: qué se puede hacer",
  "topbar.projectInfo": "Información del proyecto",
  "topbar.notices": "Avisos",
  "topbar.changeView": "Cambiar vista",
  "topbar.viewAsCards": "Ver como tarjetas (sin mapa)",
  "topbar.viewOnMap": "Ver en el mapa",
  "topbar.editProject": "Editar proyecto",
  "topbar.calibration": "Calibración de parámetros",
  "topbar.calculate": "Calcular",
  "topbar.calculating": "Calculando... {n}/{total}",
  "topbar.cannotCalculate": "No se puede calcular: {reasons}",
  "topbar.calculateTitle": "Elegir fechas y opciones de acuíferos, y calcular",
  "topbar.recalculate": "Volver a calcular",
  "topbar.recalculateTitle": "Volver a calcular con las mismas opciones (sin abrir el diálogo)",
  "topbar.results": "Resultados",
  "topbar.subBasins": "subcuencas",
  "topbar.searchSubBasin": "Buscar subcuenca",
  "topbar.searchSubBasinPlaceholder": "Nombre de la subcuenca…",
  "topbar.searchSubBasinNoMatches": "Sin coincidencias",
  "topbar.aemetDownloads": "Descargas de AEMET",
  "topbar.downloadInProgressWarningTitle": "Hay descargas de AEMET en curso",
  "topbar.downloadInProgressWarningMessage":
    "Si cierras el proyecto ahora, la(s) descarga(s) de AEMET en curso se interrumpirán y no se aplicarán. ¿Seguro que quieres cerrar?",
  "topbar.closeAnyway": "Cerrar de todos modos",

  // --- ConfirmDeleteDialog.tsx ---
  "confirmDelete.deletePermanently": "Eliminar definitivamente",
  "confirmDelete.defaultMessage":
    'Se eliminará "{name}" de forma permanente — no se puede deshacer. Si viene de una capa GIS, podrás recuperar su geometría básica volviendo a importarla o dibujarla, pero tendrás que reconfigurarla (zona, topología, ajustes...) desde cero.',

  // --- ExportChoiceDialog.tsx ---
  "exportChoice.title": "Exportar copia",
  "exportChoice.ehdzTitle": "Copia portátil (.ehdz)",
  "exportChoice.ehdzDescription":
    "Copia de seguridad de este proyecto tal cual está en esta app — para moverlo a otro navegador/equipo o guardarlo aparte. Solo esta app puede volver a abrirlo.",
  "exportChoice.csvTitle": "Ficheros CSV (.zip)",
  "exportChoice.csvDescription":
    "Los mismos ficheros que escribe el programa de escritorio para su motor de cálculo (cuencas, zonas, parámetros, acuíferos...), comprimidos en un .zip — para revisarlos, usarlos con otra herramienta o con el EvalHid de escritorio. Incluye un informe con cualquier dato incompleto detectado.",
  "exportChoice.generating": "Generando el archivo…",
  "exportChoice.error": "No se ha podido generar el archivo: {error}",

  // --- SettingsMenu.tsx ---
  "themeToggle.toDarkMode": "Cambiar a modo oscuro",
  "themeToggle.toLightMode": "Cambiar a modo claro",
  "settings.title": "Ajustes",
  "settings.driveNeedsReconnectTitle": "Ajustes — Google Drive necesita reconectarse",
  "settings.theme": "Tema",
  "settings.version": "Versión",
  "settings.drive": "Google Drive",
  "settings.driveConnect": "Conectar",
  "settings.driveConnecting": "Conectando…",
  "settings.driveDisconnect": "Desconectar",

  // --- driveSyncStore.ts / UploadExistingProjectsDialog.tsx ---
  "driveSync.notConfigured": "Google Drive no está configurado en este despliegue.",
  "driveSync.signInTimeout":
    "Se agotó el tiempo esperando el inicio de sesión de Google. Inténtalo de nuevo.",
  "driveSync.noAccessToken": "Google no ha devuelto acceso a Drive. Inténtalo de nuevo.",
  "driveSync.uploadExistingTitle": "¿Sincronizar los proyectos existentes con Drive?",
  "driveSync.unsyncedCount":
    "Hay 1 proyecto guardado en este ordenador que todavía no está en Drive.",
  "driveSync.unsyncedCountPlural":
    "Hay {n} proyectos guardados en este ordenador que todavía no están en Drive.",
  "driveSync.uploadAll": "Subir todos",
  "driveSync.notNow": "Ahora no",
  "driveSync.statusSynced": "Sincronizado con Google Drive",
  "driveSync.statusSyncing": "Sincronizando con Google Drive…",
  "driveSync.statusError": "No se ha podido sincronizar con Google Drive",
  "driveSync.useDriveVersion": "Usar la versión de Drive",
  "driveSync.conflictTooltip":
    "Google Drive tiene una versión más reciente de este proyecto — haz clic para usarla (esto sobrescribe la copia local).",

  // --- InfoPopover.tsx ---
  "infoPopover.title": "Proyecto cargado",
  "infoPopover.subBasins": "Subcuencas",
  "infoPopover.aquifers": "Acuíferos",
  "infoPopover.outletPoints": "Puntos de desagüe",
  "infoPopover.models": "Modelos lluvia-escorrentía",
  "infoPopover.precipitation": "Precipitación",
  "infoPopover.etp": "ETP",
  "infoPopover.temperature": "Temperatura",
  "infoPopover.pumping": "Bombeos",
  "infoPopover.notAvailable": "no disponible",
  "infoPopover.notProvidedOptionalSingular": "no aportada (opcional)",
  "infoPopover.notProvidedOptionalPlural": "no aportados (opcional)",
  "infoPopover.dateRange": "{from} a {to} ({n} filas)",

  // --- NoticesPopover.tsx ---
  "noticesPopover.noWarnings": "Sin avisos.",

  // --- ErrorBoundary.tsx ---
  "errorBoundary.title": "Algo ha ido mal",
  "errorBoundary.message":
    'La aplicación ha encontrado un error inesperado y no puede continuar en este estado. Hace falta recargar — tu proyecto se guarda automáticamente, así que lo encontrarás en "Tus proyectos" al volver a abrir la app.',
  "errorBoundary.reload": "Recargar",

  // --- ExampleBanner.tsx ---
  "exampleBanner.warning":
    "Estás viendo un ejemplo — los cambios no se guardan a menos que hagas una copia.",

  // --- MitecoAttributionBanner.tsx ---
  "mitecoAttributionBanner.warning":
    "Los polígonos de {kind} mostrados proceden del Ministerio para la Transición Ecológica y el Reto Demográfico (MITECO) — información de uso libre y gratuito citando al MITECO como autor y titular.",
  "mitecoAttributionBanner.basinsType": "cuencas hidrográficas oficiales",
  "mitecoAttributionBanner.subBasinsType": "subcuencas hidrográficas oficiales",
  "mitecoAttributionBanner.viewSource": "Ver fuente ↗",

  // --- MitecoAemetBanner.tsx ---
  "mitecoAemetBanner.warning":
    "Se están descargando automáticamente, en segundo plano, las series de precipitación, temperatura y ETP de la malla ROCIO de AEMET (1951-2022) para las nuevas subcuencas. Puedes seguir configurando puntos de desagüe y zonas mientras tanto.",

  // --- Landing.tsx ---
  "landing.tagline":
    "Evaluación de aportaciones de cuencas y recargas de acuíferos, en el navegador.",
  "landing.yourProjects": "Tus proyectos",
  "landing.driveNotConnectedHint":
    "Estos proyectos solo están guardados en este ordenador. Conecta Google Drive desde Ajustes para tenerlos también en otros ordenadores.",
  "landing.openProject": 'Abrir "{name}"',
  "landing.renameAria": 'Renombrar "{name}"',
  "landing.rename": "Renombrar",
  "landing.loading": "Cargando…",
  "landing.openFromFolder": "Abrir proyecto desde carpeta…",
  "landing.createNew": "Crear proyecto nuevo…",
  "landing.importCopy": "Importar copia…",
  "landing.orTryAnExample": "O prueba con un ejemplo",

  // --- examples.ts ---
  "examples.aguedaGis.title": "Con GIS",
  "examples.aguedaGis.description":
    "14 subcuencas reales de la cuenca del Águeda, con shapefile enlazado al mapa.",
  "examples.aguedaSinGis.title": "Sin GIS",
  "examples.aguedaSinGis.description":
    "El mismo proyecto, sin capa de mapa: subcuencas en tarjetas por zona.",

  // --- ChooseEhdDialog.tsx ---
  "chooseEhdDialog.title": "Varios proyectos en esta carpeta",
  "chooseEhdDialog.message":
    "Esta carpeta tiene más de un fichero .ehd (también se han buscado en las subcarpetas) — elige cuál abrir:",

  // --- CalculationOptionsDialog.tsx ---
  "calculationOptions.title": "Opciones de cálculo",
  "calculationOptions.startDate": "Fecha inicial",
  "calculationOptions.availableRange":
    "Rango disponible: {min} — {max} (según la precipitación cargada).",
  "calculationOptions.endDate": "Fecha final",
  "calculationOptions.negativeAquifers": "Negativos en acuíferos",
  "calculationOptions.modelPumping": "Modelar bombeos",
  "calculationOptions.errorNoDates": "No hay fechas en la serie de precipitación cargada.",
  "calculationOptions.errorStartDateOutOfRange":
    "La fecha inicial debe estar entre {min} y {max} (rango de la precipitación cargada).",
  "calculationOptions.errorEndDateOutOfRange": "La fecha final debe estar entre {min} y {max}.",
  "calculationOptions.errorEndBeforeStart": "La fecha final no puede ser anterior a la inicial.",

  // --- Legend.tsx ---
  "legend.normalSubBasin": "Subcuenca normal",
  "legend.subBasinWithSnow": "Subcuenca con nieve",
  "legend.subBasinWithIssues": "Subcuenca con problemas",
  "legend.unlinked": "Sin enlazar con el modelo",
  "legend.outletPoint": "Punto de desagüe",
  "legend.aquifer": "Acuífero",
  "legend.title": "Leyenda",
  "legend.show": "Mostrar leyenda del mapa",
  "legend.hide": "Ocultar leyenda",

  // --- AssignmentColorLegend.tsx ---
  "assignmentLegend.unassignedLabel": "Sin asignar",

  // --- basemap.ts (LayersControl, SubBasinMap.tsx) ---
  "basemap.streets": "Calles",
  "basemap.satellite": "Vista aérea",
  "basemap.terrain": "Terreno",

  // --- MapDrawing.tsx ---
  "drawing.subBasin": "subcuenca",
  "drawing.aquifer": "acuífero",
  "drawing.outletPoint": "punto de desagüe",

  // --- SubBasinMap.tsx ---
  "map.noName": "Sin nombre",
  "map.area": "Superficie: {km2} km²",
  "map.zone": "Zona: {zone} ({model})",
  "map.snowZone": "Zona de nieve: {zone} ({model})",
  "map.outletColon": "Punto de desagüe: {name}",
  "map.aquiferColon": "Acuífero: {name}",
  "map.clickToPlace": "Haz clic en el mapa para colocar el {tool}.",
  "map.vertexSingular": "vértice",
  "map.vertexPlural": "vértices",
  "map.vertexCount": '{n} {word} — haz clic para añadir más, "Finalizar" con al menos 3.',
  "map.finish": "Finalizar",
  "map.addAsSubBasin": 'Añadir "{name}" como subcuenca',

  // --- SubBasinCanvas.tsx ---
  "canvas.noGisLayer":
    'Sin capa GIS enlazada — subcuencas agrupadas por zona, sin geometría real. Puedes vincular un shapefile desde "Editar proyecto".',
  "canvas.noProjectSubBasins": "Este proyecto todavía no tiene subcuencas.",
  "canvas.snowZonesTab": "Zonas de nieve",
  "canvas.noZoneAssigned": "Sin zona asignada",
  "canvas.noSnowZoneAssigned": "Sin zona de nieve asignada",
  "canvas.noOutletAssigned": "Sin punto de desagüe asignado",
  "canvas.noAquiferAssigned": "Sin acuífero asignado",
  "canvas.noSubBasins": "Sin subcuencas",
  "canvas.editEntity": "Editar",
  "canvas.collapseGroup": "Contraer",
  "canvas.expandGroup": "Expandir",
  "canvas.multiSelectHelp":
    "Mantén Mayús y arrastra sobre varias subcuencas para seleccionarlas (Ctrl+arrastrar quita), y luego arrástralas juntas a una zona, un punto de desagüe o un acuífero.",
  "canvas.oneSelected":
    "{n} subcuenca seleccionada — arrástrala a una zona, un punto de desagüe o un acuífero.",
  "canvas.multipleSelected":
    "{n} subcuencas seleccionadas — arrástralas a una zona, un punto de desagüe o un acuífero.",

  // --- comunes (más) ---
  "common.downloadCsv": "Descargar CSV",

  // --- DetailChart.tsx ---
  "detailChart.title": "{description} — detalle interno (resultados parciales)",

  // --- ContributionChart.tsx / AggregatedContributionChart.tsx ---
  "contributionChart.title": "{title} — aportación por {period} ({unit})",

  // --- PasteDailySeries.tsx ---
  "pasteSeries.noDataYet": "— sin datos todavía",
  "pasteSeries.rangeDaily": "{from} a {to} ({n} días)",
  "pasteSeries.rangeMonthly": "{from} a {to} ({n} meses)",
  "pasteSeries.formatHint": "Formato: {format}",
  "pasteSeries.formatHintUnknownScale":
    "Aún no se ha fijado la escala del proyecto — formatos aceptados: {formats}. Se detecta automáticamente a partir de las 2 primeras fechas.",
  "pasteSeries.noDataSuffix": ", {n} sin dato",
  "pasteSeries.gapsSuffix": ", {n} huecos en las fechas",
  "pasteSeries.average": "Media: {mean} · Máx: {max} · Mín: {min}",
  "pasteSeries.replaceWithAnother": "Sustituir por otra serie…",
  "pasteSeries.pasteSeries": "Pegar serie…",
  "pasteSeries.copy": "Copiar",
  "pasteSeries.copied": "¡Copiado!",
  "pasteSeries.deleteSeries": "Borrar serie",
  "pasteSeries.chartView": "Ver como gráfica",
  "pasteSeries.tableView": "Ver como tabla (editar valores)",
  "pasteSeries.value": "Valor",
  "pasteSeries.noValue": "sin dato",
  "pasteSeries.goToDate": "Ir a la fecha",
  "pasteSeries.confirmDeleteTitle": "¿Eliminar esta serie?",
  "pasteSeries.confirmDeleteText":
    'Se borrará permanentemente la serie de "{name}". No se puede deshacer.',
  "pasteSeries.confirmUnlinkText":
    'Esta columna de datos está compartida con otra subcuenca o acuífero (posible en proyectos antiguos, enlazados por índice) — solo se desvinculará "{name}", los datos no se borrarán.',

  // --- ResultsPanel.tsx ---
  "resultsPanel.close": "Cerrar resultados",

  // --- CalculationResults.tsx ---
  "units.title": "Unidades",
  "units.km2": "Km² (× 0.001)",
  "units.ha": "ha (× 0.00001)",
  "units.other": "Otras...",
  "units.namePlaceholder": "Nombre (p.ej. Hm³)",
  "units.coefficientPlaceholder": "Coeficiente",
  "resultsPanel.totalPerPeriod": "Total ({unit}/{period})",
  "resultsPanel.granularityTitle": "Ver por",
  "resultsPanel.granularityDaily": "Diario",
  "resultsPanel.granularityMonthly": "Mensual",
  "resultsPanel.granularityAnnual": "Anual",
  "resultsPanel.periodDay": "día",
  "resultsPanel.periodMonth": "mes",
  "resultsPanel.periodYear": "año",
  "resultsPanel.surface": "Superficial",
  "resultsPanel.groundwater": "Subterránea",
  "resultsPanel.noBreakdown": "Sin desglose",
  "resultsPanel.zone": "Zona",
  "resultsPanel.subBasin": "Subcuenca",
  "resultsPanel.calculatedCount": "{n} de {total} subcuencas calculadas.",
  "resultsPanel.noColumnWarning":
    "Las subcuencas no calculadas no tienen datos de entrada de precipitación o ETP.",
  "resultsPanel.aquiferRecharge": "Recarga",
  "resultsPanel.aquiferPumping": "Bombeo",
  "resultsPanel.aquiferDischarge": "Aportación del acuífero",
  "resultsPanel.oneAquifer": "un acuífero",
  "resultsPanel.alsoCanSelect": "También puedes seleccionar {what} clicando en el mapa.",
  "resultsPanel.oneSubBasin": "una subcuenca",
  "resultsPanel.oneOutletPoint": "un punto de desagüe",
  "resultsPanel.areaKm2": "Superficie (km²)",
  "resultsPanel.mmPerDay": "mm/día",
  "resultsPanel.mmPerMonth": "mm/mes",
  "resultsPanel.unitPerPeriod": "{unit}/{period}",
  "resultsPanel.hideDetail": "Ocultar detalle",
  "resultsPanel.viewDetail": "Ver detalle",

  // --- EditBar.tsx ---
  "panels.subBasins": "Subcuencas",
  "panels.zones": "Zonas",
  "panels.inputSeries": "Series de entrada",
  "panels.layers": "Capas auxiliares",
  "editBar.closeEdit": "Cerrar edición",

  // --- SubBasinsPanel.tsx ---
  "subBasinsPanel.createManualTitle": "Crear manualmente",
  "subBasinsPanel.drawTitle": "Dibujar",
  "subBasinsPanel.csvTitle": "CSV",
  "subBasinsPanel.linkGisLayer": "Vincular capa GIS",
  "subBasinsPanel.catalog": "Todas las subcuencas",
  "subBasinsPanel.exportCsv": "Exportar a CSV",
  "subBasinsPanel.oneSelected": "{n} subcuenca seleccionada",
  "subBasinsPanel.multipleSelected": "{n} subcuencas seleccionadas",
  "subBasinsPanel.clearSelection": "Limpiar selección",
  "subBasinsPanel.snowZone": "Zona de nieve",
  "subBasinsPanel.deleteSelected": "Eliminar seleccionadas",
  "subBasinsPanel.addManual": "Añadir subcuenca manual",
  "subBasinsPanel.noGis": " (sin GIS)",
  "subBasinsPanel.editTitle": "Editar subcuenca",
  "subBasinsPanel.deletePermanentTitle": "Eliminar subcuenca (permanente)",
  "subBasinsPanel.deleteDialogTitle": "Eliminar subcuenca",
  "subBasinsPanel.deleteDialogPluralTitle": "Eliminar subcuencas",
  "subBasinsPanel.addFromMiteco": "Añadir desde MITECO",
  "subBasinsPanel.mitecoBasinsLayer": "Cuencas",
  "subBasinsPanel.mitecoSubBasinsLayer": "Subcuencas",
  "subBasinsPanel.mitecoHelp":
    "Clic para (des)marcar un polígono, o arrastra un rectángulo (Mayús. suma, Ctrl resta) para varios a la vez.",
  "subBasinsPanel.mitecoOneSelected": "{n} del MITECO seleccionada",
  "subBasinsPanel.mitecoMultipleSelected": "{n} del MITECO seleccionadas",
  "subBasinsPanel.addMitecoSelection": "Añadir como subcuencas",
  "subBasinsPanel.mitecoAlreadyAdded": "Ya añadida a este proyecto",

  // --- GisLayerPanel.tsx ---
  "gisLayerPanel.layerAlreadyExists": "Ya hay una capa cargada — puedes sustituirla subiendo otra.",
  "gisLayerPanel.uploadShapefile": "Sube el shapefile de subcuencas o un GeoJSON.",
  "gisLayerPanel.modeLink": "Vincular",
  "gisLayerPanel.modeRecover": "Recuperar eliminadas",
  "gisLayerPanel.linkColumn": "Columna de enlace",
  "gisLayerPanel.linkColumnHelp":
    "Un código que identifique a cada subcuenca (el mismo que se usó al crear el proyecto) o, si no hay ninguna columna así, un nombre que coincida con la descripción de la subcuenca.",
  "gisLayerPanel.linkedCount": "{n} de {total} subcuencas enlazadas con esta columna.",
  "gisLayerPanel.linkLayer": "Vincular capa",
  "gisLayerPanel.allAlreadyPresent":
    "Todas las geometrías de este fichero ya están en el proyecto — no hay nada que recuperar.",
  "gisLayerPanel.missingCount":
    "{n} de {total} geometrías no están ya en el proyecto (comparando por geometría, no por nombre) — probablemente subcuencas eliminadas. Se recuperan como nuevas, sin la configuración que tuvieran antes (zona, topología, ajustes...).",
  "gisLayerPanel.subBasinName": "Nombre de la subcuenca",
  "gisLayerPanel.recoverOne": "Recuperar {n} subcuenca",
  "gisLayerPanel.recoverMultiple": "Recuperar {n} subcuencas",

  // --- LinkGisPanel.tsx ---
  "linkGisPanel.linkMode": "Vincular",
  "linkGisPanel.createMode": "Crear nuevos",
  "linkGisPanel.uploadShapefileOf": "Sube el shapefile de {plural} o un GeoJSON.",
  "linkGisPanel.linkColumnHelp":
    "Un código que identifique a cada {singular} (el mismo que se usó al crear el proyecto) o, si no hay ninguna columna así, un nombre que coincida con el nombre real.",
  "linkGisPanel.linkedCount": "{n} de {total} {plural} enlazados con esta columna.",
  "linkGisPanel.allAlreadyLinked":
    "Todas las geometrías de este fichero ya están enlazadas con algún {singular} del proyecto — no hay nada nuevo que crear.",
  "linkGisPanel.missingCount":
    "{n} de {total} geometrías no están enlazadas con ningún {singular} del proyecto (comparando por geometría, no por nombre) — se pueden crear como {plural} nuevos.",
  "linkGisPanel.nameOf": "Nombre del {singular}",
  "linkGisPanel.createButton": "Crear {n} {name}",

  // --- AquifersPanel.tsx ---
  "common.aquiferPlural": "acuíferos",
  "aquifersPanel.topologyTitle": "Acuífero de desagüe: a qué acuífero vierte cada subcuenca",
  "aquifersPanel.assignAquifer": "Asignar acuífero",
  "aquifersPanel.catalog": "Todos los acuíferos",

  // --- OutletPointsPanel.tsx ---
  "common.outletPointPlural": "puntos de desagüe",
  "outletPointsPanel.topologyTitle": "Desagüe superficial: a qué punto vierte cada subcuenca",
  "outletPointsPanel.assignOutlet": "Asignar desagüe",
  "outletPointsPanel.catalog": "Todos los puntos de desagüe",

  // --- SeriesPanel.tsx ---
  "seriesPanel.formatHelpSubBasin":
    'CSV con separador ";", fecha {format} y una columna por subcuenca — el nombre de columna debe coincidir con el nombre de la subcuenca. Ojo: esto sustituye la serie completa de TODAS las subcuencas, no solo añade o actualiza columnas — las subcuencas que falten en el archivo se quedan sin datos.',
  "seriesPanel.formatHelpAquifer":
    'CSV con separador ";", fecha {format} y una columna por acuífero — el nombre de columna debe coincidir con el nombre del acuífero. Ojo: esto sustituye la serie completa de TODOS los acuíferos, no solo añade o actualiza columnas — los acuíferos que falten en el archivo se quedan sin datos.',
  "seriesPanel.mapSelectionHelpSubBasin":
    "Este gráfico muestra la subcuenca seleccionada en el mapa o en la vista de tarjetas (la primera con datos, hasta que selecciones otra).",
  "seriesPanel.mapSelectionHelpPumping":
    "Este gráfico muestra el acuífero o la subcuenca seleccionados en el mapa o en la vista de tarjetas (el primero con datos, hasta que selecciones otro) — el bombeo puede enlazarse a cualquiera de los dos.",

  // --- SeriesChart.tsx ---
  "seriesPanel.noSeriesYet": "Aún no se ha cargado esta serie.",
  "seriesPanel.showing": "Mostrando: {name}",
  "seriesPanel.loading": "Cargando…",

  "seriesPanel.fetchFromAemet": "Obtener series de AEMET",
  "seriesPanel.gapWarningOne":
    "1 subcuenca tiene un hueco en su precipitación o ETP — probablemente se quedó a medias en una ampliación de rango anterior.",
  "seriesPanel.gapWarningMultiple":
    "{n} subcuencas tienen huecos en su precipitación o ETP — probablemente se quedaron a medias en una ampliación de rango anterior.",
  "seriesPanel.gapWarningComplete": "Completar estas {n}",
  "seriesPanel.gapWarningCompleting": "Completando {done}/{total}…",

  // --- NameDrawingDialog.tsx ---
  "nameDrawingDialog.new": "Nueva {tool}",

  // --- FieldsPreviewTable.tsx ---
  "fieldsPreviewTable.showingFirst": "Mostrando las primeras 8 de {n} geometrías.",

  // --- DrawButton.tsx ---
  "drawButton.draw": "Dibujar {tool}",
  "drawButton.cancelDrawing": "Cancelar dibujo",

  // --- EditSubBasinDialog.tsx ---
  "editSubBasinDialog.title": 'Editar subcuenca "{name}"',
  "editSubBasinDialog.basicData": "Datos básicos",
  "editSubBasinDialog.precipitationAdjustment": "Ajuste de precipitación",
  "editSubBasinDialog.etpAdjustment": "Ajuste de ETP",
  "editSubBasinDialog.temperatureAdjustment": "Ajuste de temperatura",
  "editSubBasinDialog.fetchFromAemet": "Obtener de AEMET",
  "editSubBasinDialog.aemetNoGeometry":
    "Esta subcuenca no tiene geometría enlazada — no se puede buscar la estación más cercana.",
  "editSubBasinDialog.aemetSource":
    "Datos de AEMET: estación {name} (a {km} km), consultado el {date}.",
  "editSubBasinDialog.aemetSourceRocio":
    "Datos de la malla ROCIO de AEMET (actualizada por AEMET el {aemetDate}): {n} celdas dentro de la subcuenca. Consultado el {date}.",
  "editSubBasinDialog.aemetSourceRocioNoDate":
    "Datos de la malla ROCIO de AEMET: {n} celdas dentro de la subcuenca. Consultado el {date}.",
  "editSubBasinDialog.viewAemetReport": "Ver informe de AEMET",
  "editSubBasinDialog.aemetReportTitle": "Informe de AEMET",

  // --- aemet.ts / rocio.ts (códigos de error de functions/api/ — el servidor no tiene acceso a i18n, ver aemetProtocol.ts) ---
  "aemetFetch.notConfigured": "Este despliegue no tiene configurada la clave de AEMET.",
  "aemetFetch.rateLimited":
    "Demasiadas peticiones a AEMET desde esta conexión — espera un momento antes de reintentar.",
  "aemetFetch.upstreamError": "AEMET respondió con un error ({status}).",
  "aemetFetch.unexpectedResponse": "Respuesta inesperada de AEMET.",
  "aemetFetch.notFound": "Los datos solicitados todavía no están disponibles en este despliegue.",

  // --- AemetFetchDialog.tsx ---
  "aemetDialog.title": "Obtener series de AEMET",
  "aemetDialog.legalNotice":
    "Los datos climatológicos mostrados proceden de la Agencia Estatal de Meteorología (AEMET) — uso autorizado citando a AEMET como autora de la información.",
  "aemetDialog.viewLegalNotice": "Ver nota legal ↗",
  "aemetDialog.pending": "En cola — se iniciará cuando le toque el turno.",
  "aemetDialog.searchingStations": "Buscando estaciones cercanas…",
  "aemetDialog.searchingCells": "Buscando celdas de la malla ROCIO…",
  "aemetDialog.stationsError": "No se pudo obtener el listado de estaciones de AEMET: {error}",
  "aemetDialog.downloadError": "No se pudo completar la descarga: {error}",
  "aemetDialog.noStations": "No se ha encontrado ninguna estación de AEMET a menos de 100 km.",
  "aemetDialog.noRocioCoverage":
    "Esta subcuenca queda fuera de la cobertura de la malla ROCIO (España peninsular y Baleares).",
  "aemetDialog.mainStation": "Estación principal: {name} (a {km} km, {elevation} m de altitud).",
  "aemetDialog.backupStations": "Estaciones de respaldo, por si faltan datos algún día: {list}.",
  "aemetDialog.rocioCellsOne": "Se usará 1 celda de la malla ROCIO dentro de esta subcuenca.",
  "aemetDialog.rocioCellsMany": "Se usarán {n} celdas de la malla ROCIO dentro de esta subcuenca.",
  "aemetDialog.rangeTitle": "Rango de fechas",
  "aemetDialog.rocioRangeLimit":
    "La malla ROCIO solo tiene datos del 1951-01-01 al 2022-12-31 — fuera de ese rango los días quedarán sin dato.",
  "aemetDialog.rangeFixedNotice":
    "Ya hay una serie de precipitación cargada del {from} al {to} — se pedirá exactamente ese rango a AEMET para que encaje.",
  "aemetDialog.startDate": "Fecha inicial",
  "aemetDialog.endDate": "Fecha final",
  "aemetDialog.invalidRange": "La fecha final debe ser posterior a la inicial.",
  "aemetDialog.methodTitle": "Método",
  "aemetDialog.methodNearest": "Estación más cercana (con relleno de las siguientes)",
  "aemetDialog.methodIdw": "Promedio ponderado por distancia (IDW)",
  "aemetDialog.methodNearestHelp":
    "Cada día usa la estación más cercana que tenga dato; si esa falla, pasa a la siguiente más cercana.",
  "aemetDialog.methodIdwHelp":
    "Cada día combina TODAS las estaciones candidatas que tengan dato, ponderadas por el cuadrado inverso de su distancia (más peso cuanto más cerca) — suele comportarse mejor que la estación más cercana cuando esta queda algo lejos o el terreno es complejo.",
  "aemetDialog.methodRocio": "Malla ROCIO (rejilla de AEMET, 5 km)",
  "aemetDialog.methodRocioHelp":
    "Precipitación y temperatura diarias de la rejilla interpolada de AEMET (ROCIO_IBEB, ~5 km, 1951 en adelante).",
  "aemetDialog.estimateOneMinute": "Tardará alrededor de 1 minuto.",
  "aemetDialog.estimateMinutes": "Tardará unos {min} minutos.",
  "aemetDialog.startDownload": "Empezar descarga",
  "aemetDialog.downloadProgress": "Descargando del {from} al {to} — llevamos hasta {current}.",
  "aemetDialog.continuesInBackground":
    "Puedes cerrar esta ventana — la descarga seguirá en segundo plano y se aplicará sola al terminar. Para volver a verla, usa las descargas de AEMET en la barra superior.",
  "aemetDialog.noData": "sin dato",
  "aemetDialog.sourceOneDay": "{source}: 1 día",
  "aemetDialog.sourceDays": "{source}: {n} días",
  "aemetDialog.reportTitle": "Antes de aplicar",
  "aemetDialog.failedWindows":
    "{n} periodos no se pudieron completar — esos días quedarán sin dato:",
  "aemetDialog.failedWindowRange": "{from} a {to}",
  "aemetDialog.retryFailedWindows": "Reintentar {n} periodos fallidos",
  "aemetDialog.retrying": "Reintentando… {done} de {total}.",
  "aemetDialog.appliedAutomatically":
    "Aplicado automáticamente al terminar la descarga — este resumen queda guardado, consúltalo luego con el botón de informe.",
  "aemetDialog.retryApply": "Reintentar aplicar",
  "aemetDialog.subBasinDeleted":
    "La subcuenca se eliminó antes de que terminara la descarga — no hay dónde aplicar los datos.",

  // --- Pagination.tsx ---
  "pagination.first": "Primera página",
  "pagination.previous": "Página anterior",
  "pagination.page": "Página",
  "pagination.of": "de {total}",
  "pagination.next": "Página siguiente",
  "pagination.last": "Última página",

  // --- AemetBulkFetchDialog.tsx ---
  "aemetBulkDialog.title": "Obtener series de AEMET",
  "aemetBulkDialog.chooseMode": "¿Qué quieres hacer?",
  "aemetBulkDialog.completeMode": "Completar subcuencas que faltan ({n})",
  "aemetBulkDialog.extendMode": "Ampliar el rango de fechas",
  "aemetBulkDialog.back": "← Volver",
  "aemetBulkDialog.nothingToDo":
    "Este proyecto no tiene subcuencas con datos pendientes ni con datos de AEMET que ampliar.",
  "aemetBulkDialog.oneMissing": "1 subcuenca con datos pendientes.",
  "aemetBulkDialog.multipleMissing": "{n} subcuencas con datos pendientes.",
  "aemetBulkDialog.allComplete": "Todas las subcuencas ya tienen sus series de AEMET.",
  "aemetBulkDialog.rangeHelp":
    "Este proyecto todavía no tiene ninguna serie cargada — elige el rango de fechas a pedir a AEMET, se usará para todas las descargas de aquí abajo.",
  "aemetBulkDialog.downloadAll": "Descargar todas ({n})",
  "aemetBulkDialog.downloadingAll": "Descargando... {done}/{total}",
  "aemetBulkDialog.download": "Descargar",
  "aemetBulkDialog.noCoverage": "Fuera de la cobertura de AEMET.",
  "aemetBulkDialog.retry": "Reintentar",
  "aemetBulkDialog.completed": "Completado ✓",
  "aemetBulkDialog.completedWithGaps":
    "Completado, {n} ventanas sin datos — revisa el informe de la subcuenca.",
  "aemetBulkDialog.currentRange": "Rango actual de la serie: {from} a {to}.",
  "aemetBulkDialog.newRangeTitle": "Nuevo rango a añadir",
  "aemetBulkDialog.newRangeHelp":
    "El nuevo rango continúa justo donde termina (o antes de donde empieza) el actual, sin dejar huecos en el calendario — solo hace falta elegir el otro extremo.",
  "aemetBulkDialog.direction": "Dirección",
  "aemetBulkDialog.directionForward": "Años posteriores (traer los datos al día)",
  "aemetBulkDialog.directionBackward": "Años anteriores",
  "aemetBulkDialog.currentData": "Datos actuales: {from} a {to}",
  "aemetBulkDialog.extend": "Ampliar",
  "aemetBulkDialog.extendAll": "Ampliar todas ({n})",
  "aemetBulkDialog.extendingAll": "Ampliando... {done}/{total}",
  "aemetBulkDialog.extendingAllRange":
    "Descargando el rango {from} a {to} para todas las subcuencas.",

  // --- AemetJobsPopover.tsx ---
  "aemetJobsPopover.title": "Descargas de AEMET",
  "aemetJobsPopover.phasePending": "Pendiente de inicio",
  "aemetJobsPopover.phaseSearchingStations": "Buscando estaciones…",
  "aemetJobsPopover.phaseSearchingCells": "Buscando celdas ROCIO…",
  "aemetJobsPopover.phaseNoStations": "Sin estaciones cercanas",
  "aemetJobsPopover.phaseNoCoverage": "Sin cobertura ROCIO",
  "aemetJobsPopover.phaseError": "Error",
  "aemetJobsPopover.phaseWaitingRange": "Esperando rango de fechas",
  "aemetJobsPopover.phaseApplied": "Aplicado",
  "aemetJobsPopover.cancel": "Cancelar",
  "aemetJobsPopover.remove": "Quitar de la lista",
  "aemetJobsPopover.clearFinished": "Limpiar terminadas",

  // --- NewProject.tsx ---
  "newProject.outletPoints": "Ptos. Desagüe",
  "newProject.review": "Revisión",
  "newProject.title": "Crear proyecto nuevo",
  "newProject.projectName": "Nombre del proyecto",
  "newProject.wizardExplanation":
    'Puedes terminar el asistente en cualquier momento con "Finalizar" — el proyecto se crea con lo que hayas configurado hasta ese punto, y lo que falte (más subcuencas, zonas, series…) se puede añadir después, editando el proyecto ya creado.',
  "newProject.errorCount": "{n} error",
  "newProject.errorsCount": "{n} errores",
  "newProject.warningCount": "{n} aviso",
  "newProject.warningsCount": "{n} avisos",

  // --- Step1SubBasins.tsx ---
  "step1.uploadShapefile": "Sube un shapefile o geojson de subcuencas.",
  "step1.geometriesFound": "{n} geometrías encontradas. ¿Qué columna es cada cosa?",
  "step1.importButton": "Importar {n} subcuencas",
  "step1.inputSummary": "{name} ({area} km²)",
  "step1.createButton": "Crear {n} subcuencas",
  "step1.fromShapefile": "Desde shapefile",
  "step1.createManually": "Crear a mano",
  "step1.csvTab": "CSV",
  "step1.noSubBasinsTab": "Sin subcuencas",
  "step1.subBasinsCreated": "Ya tienes {n} subcuencas creadas.",
  "step1.editHelp":
    "Puedes cambiar el nombre o la superficie de cualquiera, eliminar alguna o añadir otra antes de seguir — nada de esto se pierde si retrocedes a este paso más adelante.",
  "step1.addAnother": "Añadir otra",
  "step1.uploadCsvButton": "Subir CSV",
  "step1.csvHelp": "Una fila por subcuenca, con cabecera: nombre,superficie.",
  "step1.csvImported": "{n} subcuencas importadas del CSV.",
  "step1.mitecoTab": "MITECO",
  "step1.mitecoHelp":
    "Haz clic en una cuenca del mapa para importar automáticamente todas sus subcuencas MITECO.",
  "step1.mitecoSearchingIn": "Buscando subcuencas en {name}…",
  "step1.mitecoNoneFoundIn": "{name}: no se ha encontrado ninguna subcuenca MITECO dentro.",
  "step1.mitecoSummary": "{name} — {n} subcuencas encontradas.",
  "step1.mitecoManyFound":
    "Son muchas ({n}) — importarlas y descargar sus series de AEMET puede tardar varios minutos.",
  "step1.mitecoRocioRangeReduced":
    'Al ser tantas subcuencas, para no sobrecargar el navegador se pedirán solo los últimos {years} años de la malla ROCIO (en vez del histórico completo desde 1951) — se puede ampliar después, subcuenca a subcuenca, desde "Obtener de AEMET".',

  // --- Step2Zones.tsx ---
  "step2.chooseModel": "Elige un modelo para la zona.",
  "step2.replicateOutletsButton": "Replicar desde puntos de desagüe",
  "step2.replicateOutletsHelp":
    "Crea una zona por cada punto de desagüe, con el modelo elegido, y la asigna a las mismas subcuencas que ya tiene ese punto.",
  "step2.replicateOutletsUpToDate":
    "Nada que replicar — todos los puntos de desagüe ya tienen su zona.",
  "step2.replicateOutletsChooseModel": "Elige un modelo antes de replicar.",
  "step2.zoneHelp":
    "Cada zona lleva un modelo (lluvia-escorrentía o de nieve) y sus parámetros por defecto, editables. Al asignar una zona a una subcuenca, esa subcuenca queda con el modelo de la zona.",
  "step2.zoneNamePlaceholder": "Nombre de la zona",
  "common.modelPlaceholder": "— modelo —",
  "step2.snowSuffix": "(nieve)",
  "step2.createZone": "Crear zona",
  "step2.deleteZonePermanent": "Eliminar zona (permanente)",
  "step2.deleteZoneDialogTitle": "Eliminar zona",
  "step2.editZoneTitle": "Editar zona",
  "common.rename": "Renombrar",
  "common.model": "Modelo",
  "step2.changeModelWarning":
    "Cambiar el modelo sustituye los parámetros y variables de estado actuales por los valores por defecto del modelo nuevo.",
  "step2.parametersOf": 'Parámetros de "{name}"',
  "step2.parameterScaleWarning":
    'Los parámetros con unidades "1/tiempo" o "tiempo" (Alfa, K0-K2, Kperc, X4...) están referidos al paso temporal del proyecto: un día en un proyecto diario, un mes en uno mensual. La app nunca los reescala automáticamente al cambiar de escala — tienen que introducirse ya calibrados para ese paso.',
  "common.parameter": "Parámetro",
  "common.value": "Valor",
  "common.units": "Unidades",
  "step2.stateVariablesOf": 'Variables de estado iniciales de "{name}"',
  "common.variable": "Variable",
  "common.initialValue": "Valor inicial",
  "step2.assignZoneTitle": "Asignar zona a subcuencas",
  "step2.mapAssignHelp":
    "Clica una subcuenca en el mapa para (des)seleccionarla, o mantén Mayús y arrastra para seleccionar varias (Ctrl+arrastrar para quitarlas).",
  "step2.mapOrCardsAssignHelp":
    "Clica una subcuenca en el mapa o en las tarjetas para (des)seleccionarla, o mantén Mayús y arrastra para seleccionar varias (Ctrl+arrastrar para quitarlas).",
  "step2.assignZoneButton": "Asignar zona",
  "step2.assignSnowZoneTitle": "Asignar zona de nieve a subcuencas",
  "step2.assignSnowZoneButton": "Asignar zona de nieve",

  // --- BulkAssigner.tsx ---
  "bulkAssigner.currentlyAssigned": "Asignado actualmente",
  "bulkAssigner.buttonWithCount": "{text} ({n})",

  // --- Step3Outlets.tsx ---
  "step3.editOutletPointTitle": "Editar punto de desagüe",
  "step3.editAquiferTitle": "Editar acuífero",
  "step3.observedSeries": "Serie observada",
  "common.outletPointPlaceholder": "— punto de desagüe —",
  "step3.deleteOutletPointPermanent": "Eliminar punto de desagüe (permanente)",
  "step3.deleteOutletPointDialogTitle": "Eliminar punto de desagüe",
  "step3.noOutletPointWarning":
    "Sin punto de desagüe, este acuífero no aparecerá en los resultados.",
  "step3.noOutletPointSuffix": " (sin punto de desagüe)",
  "step3.deleteAquiferPermanent": "Eliminar acuífero (permanente)",
  "step3.deleteAquiferDialogTitle": "Eliminar acuífero",
  "step3.title": "Puntos de desagüe",
  // Also used by OutletPointsPanel.tsx's own "replicate" button, in the editor.
  "step3.replicateZonesButton": "Replicar desde zonas",
  "step3.replicateZonesHelp":
    "Crea un punto de desagüe por cada zona y lo asigna a las mismas subcuencas que ya tiene esa zona.",
  "step3.replicateZonesUpToDate":
    "Nada que replicar — todas las zonas ya tienen su punto de desagüe.",

  // --- CalibrationPanel.tsx ---
  "calibration.title": "Calibración de parámetros",
  "calibration.outletPoint": "Punto de desagüe",
  "calibration.noPointsWithObservedSeries":
    'Ningún punto de desagüe tiene todavía una serie observada. Elige uno para abrirlo y añadir una en su pestaña "Serie observada":',
  "calibration.noOutletPointsInProject": "Este proyecto todavía no tiene ningún punto de desagüe.",
  "calibration.unitWarning":
    "La serie observada debe estar en las mismas unidades que el resultado agregado del proyecto ({unit}).",
  "calibration.zone": "Zona a calibrar",
  "calibration.noZonesWithModel":
    "Ninguna zona con un modelo asignado vierte a este punto de desagüe.",
  "calibration.parameters": "Parámetros a calibrar",
  "calibration.calibrationPeriodTitle": "Periodo de calibración",
  "calibration.calibrationStart": "Inicio",
  "calibration.calibrationEnd": "Fin",
  "calibration.calibrationDatesOutOfOrder": "El inicio debe ser anterior (o igual) al fin.",
  "calibration.validationPeriodTitle": "Periodo de validación",
  "calibration.validationStart": "Inicio",
  "calibration.validationEnd": "Fin",
  "calibration.validationDatesOutOfOrder": "El inicio debe ser anterior (o igual) al fin.",
  "calibration.insufficientCalibrationData":
    "El periodo de calibración solo tiene {n} día(s) válido(s) con dato observado (mínimo recomendado: {min}).",
  "calibration.insufficientValidationData":
    "El periodo de validación solo tiene {n} día(s) válido(s) con dato observado (mínimo recomendado: {min}).",
  "calibration.warmUpTitle": "Periodo de calentamiento",
  "calibration.warmUpHelp":
    "Simula este periodo justo antes de cada ventana (calibración y validación) para que los depósitos internos del modelo (humedad del suelo, nieve, acuífero...) alcancen un estado realista antes de empezar a puntuar — esos días se simulan pero no cuentan en la función objetivo.",
  "calibration.warmUpAmount": "Duración",
  "calibration.warmUpUnit.days": "Días",
  "calibration.warmUpUnit.weeks": "Semanas",
  "calibration.warmUpUnit.months": "Meses",
  "calibration.warmUpUnit.years": "Años",
  "calibration.warmUpClampedCalibration":
    "Solo había {n} día(s) de datos antes del periodo de calibración — se ha usado ese calentamiento más corto.",
  "calibration.warmUpClampedValidation":
    "Solo había {n} día(s) de datos antes del periodo de validación — se ha usado ese calentamiento más corto.",
  "calibration.missingSeries": "Faltan precipitación o ETP en el proyecto — no se puede calibrar.",
  // runCalibration.ts's own defensive checks — no llegan a pasar en el flujo
  // normal (el panel ya filtra a opciones válidas), pero traducidos igual
  // que cualquier otro error, en vez de texto en inglés fijo.
  "calibration.dateOutsidePrecipitation":
    "La fecha {date} está fuera de la serie de precipitación del proyecto.",
  "calibration.zoneNotFound": "Zona no encontrada, o sin modelo de lluvia-escorrentía asignado.",
  "calibration.outletPointNotFound": "Punto de desagüe no encontrado.",
  "calibration.noObservedSeriesForOutlet": "No hay serie observada para este punto de desagüe.",
  "calibration.unknownParameter": "Parámetro {code} desconocido para el modelo de esta zona.",
  "calibration.noCalibrationRangeForParameter":
    'No hay rango de calibración para el parámetro "{name}".',
  "calibration.launch": "Calibrar",
  "calibration.calculating": "Calculando…",
  "calibration.evaluations": "{n} evaluaciones",
  "calibration.stopReason.maxEvaluations": "Terminado: se alcanzó el límite de evaluaciones.",
  "calibration.stopReason.parameterSpaceConverged":
    "Terminado: la población convergió a un espacio de parámetros pequeño.",
  "calibration.stopReason.objectiveConverged":
    "Terminado: la función objetivo dejó de mejorar de forma significativa.",
  "calibration.applyParameters": "Aplicar estos parámetros",
  "calibration.discard": "Descartar",
  "calibration.progressTitle": "Progreso (mejor valor de la FO encontrado)",
  "calibration.indicatorsTable.indicatorHeader": "Indicador",
  "calibration.indicatorsTable.initialHeader": "Inicial",
  "calibration.indicatorsTable.finalHeader": "Final",
  "calibration.indicatorsTable.validationHeader": "Validación",
  "calibration.indicator.1": "Nash-Sutcliffe (NSE)",
  "calibration.indicator.2": "Nash-Sutcliffe logarítmico",
  "calibration.indicator.3": "Correlación de Pearson",
  "calibration.indicator.4": "Simetría",
  "calibration.indicator.5": "Sesgo porcentual (Pbias)",
  "calibration.indicator.6": "RSR",

  // --- Step4Topology.tsx ---
  "step4.surfaceOutlet": "Asignación a subcuencas",
  "step4.outletAquifer": "Asignación a subcuencas",

  // --- Step5Series.tsx ---
  "common.optional": "(opcional)",
  "common.chooseFile": "Elegir archivo",
  "common.chooseFiles": "Elegir archivos",
  "seriesLoader.summary": "{rows} registros, {m} de {total} {plural} con datos.",
  "seriesLoader.importFailed":
    "No se ha importado nada de este archivo — sigue como estaba antes. {error}",
  "step5.help":
    'CSV con separador ";", fecha dd/mm/aaaa y una columna por subcuenca/acuífero (el nombre de columna debe coincidir con el nombre del elemento).',

  // --- HelpPanel.tsx ---
  "helpPanel.title": "¿Qué se puede hacer?",
  "helpPanel.closeAria": "Cerrar ayuda",
  "helpPanel.searchPlaceholder": 'Buscar — p. ej. "dibujar", "avisos", "formato de fichero"...',
  "helpPanel.noResults": 'Sin resultados para "{query}".',

  // --- App.tsx ---
  "app.calculationError": "No se ha podido calcular: {message}",
  "app.calculationExcludedWarning":
    "{n} subcuenca(s) no tienen ningún dato válido de precipitación/ETP dentro del rango calculado y se han excluido del resultado: {list}.",
  "app.calculationTruncatedWarning":
    "{n} subcuenca(s) se han calculado solo hasta el primer dato vacío o inválido de precipitación/ETP: {list}.",
  "app.truncatedFromDate": "sin datos desde {date}",
  "app.savedProjectNotFoundError":
    "No se ha encontrado ese proyecto guardado — puede que se haya eliminado.",
  "app.autosaveError":
    "No se han podido guardar los últimos cambios: {message}. Sigue en memoria, pero refrescar o cerrar la pestaña los perdería — reintenta o exporta una copia (⋮ → Exportar copia) antes de seguir.",
  "app.saveAsError":
    "No se ha podido guardar la copia: {message}. El proyecto que tenías abierto sigue intacto, pero la copia nueva no se ha creado.",
  "saveErrorBanner.retry": "Reintentar",
  "saveErrorBanner.retrying": "Reintentando…",
  "app.noFileSystemAccessApiError":
    "Este navegador no soporta la File System Access API (showDirectoryPicker). Prueba con Chrome o Edge.",
  "app.defaultProjectName": "Proyecto",

  // --- engine.ts ---
  "engine.missingParameter":
    'Falta el parámetro "{key}" para el modelo "{model}" — el catálogo de parámetros del proyecto podría estar corrupto.',
  "engine.missingStateVariable":
    'Falta la variable de estado "{key}" para el modelo "{model}" — el catálogo de variables de estado del proyecto podría estar corrupto.',

  // --- subBasinState.ts (razones por las que una subcuenca no se puede calcular) ---
  "subBasinState.noZone": "Sin zona asignada.",
  "subBasinState.zoneDoesNotExist": "La zona asignada ya no existe.",
  "subBasinState.snowZoneDoesNotExist": "La zona de nieve asignada ya no existe.",
  "subBasinState.noOutlet": "Sin punto de desagüe asignado.",
  "subBasinState.outletDoesNotExist": "El punto de desagüe asignado ya no existe.",
  "subBasinState.aquiferDoesNotExist": "El acuífero asignado ya no existe.",
  "subBasinState.noPrecipEtpColumn": "Sin datos propios de precipitación/ETP.",
  "subBasinState.noTempColumn": "Sin datos de temperatura: no se simula la nieve.",

  // --- validations.ts (validateProject) ---
  "validations.noModelWarning":
    "{n} subcuenca(s) sin zona (ni modelo) asignada — quedarán fuera del cálculo.",
  "validations.gr2mNotMonthlyWarning":
    "{n} subcuenca(s) con el modelo GR2M ({list}), pero el proyecto está en escala diaria — GR2M es un modelo mensual, sus resultados no serán correctos con estos datos.",
  "validations.noOutletWarning":
    "{n} subcuenca(s) sin punto de desagüe superficial asignado (quedarán fuera de la agregación por punto de desagüe, pero sí se calculan): {list}.",
  "validations.aquifersWithoutOutletPointBlocking":
    "{n} acuífero(s) en uso sin punto de desagüe asignado: {list}.",
  "validations.duplicateName": "Nombre repetido entre una subcuenca y un acuífero: {list}.",
  "validations.noSubBasinWithModel":
    "Ninguna subcuenca tiene zona (modelo) asignada: no hay nada que simular.",
  "validations.precipSeriesNotLoaded": 'No se ha cargado la serie de "Precipitación".',
  "validations.etpSeriesNotLoaded": 'No se ha cargado la serie de "ETP".',
  "validations.precipDateGapsBlocking":
    'La serie de "Precipitación" tiene huecos de fecha: faltan {n} día(s) — {list}.',
  "validations.etpDateGapsBlocking":
    'La serie de "ETP" tiene huecos de fecha: faltan {n} día(s) — {list}.',
  "validations.temperatureDateGapsBlocking":
    'La serie de "Temperatura" tiene huecos de fecha: faltan {n} día(s) — {list}.',
  "validations.pumpingDateGapsBlocking":
    'La serie de "Bombeo" tiene huecos de fecha: faltan {n} día(s) — {list}.',
  "validations.noColumnsWarning":
    "{n} subcuenca(s) con zona asignada no tienen datos propios de precipitación/ETP y quedarán excluidas del cálculo: {list}.",
  "validations.noTempWarning":
    "{n} subcuenca(s) con zona de nieve asignada no tienen datos de temperatura: se calcularán con su modelo lluvia-escorrentía normal, sin simular la nieve: {list}.",

  // --- units.ts (chooseCustomUnits) ---
  "units.emptyNameError": "El nombre de la unidad no puede estar vacío.",
  "units.invalidCoefficientError": "El coeficiente debe ser mayor que 0.",

  // --- exportCsvProject.ts (informe.txt) ---
  "exportReport.title": "Informe de exportación a CSV",
  "exportReport.introWithFindings":
    "Se han detectado {n} incidencia(s) en los datos del proyecto. Los ficheros CSV se han generado igualmente con todos los datos disponibles — esto es solo información, ningún fichero se ha dejado de escribir ni ninguna fila se ha omitido por esto:",
  "exportReport.introNoFindings":
    "No se ha detectado ninguna incidencia en los datos del proyecto.",
  "exportReport.aquifersWithoutOutlet":
    "{n} acuífero(s) sin punto de desagüe asignado (columna Cod_Apo vacía en Ent_Acu.csv): {list}.",
  "exportReport.duplicateNames":
    "Nombre repetido entre varios elementos de {kind} (el nombre se usa como identificador en los CSV): {list}.",

  // --- ehd.ts (nombres de respaldo cuando falta el nombre real en el .ehd) ---
  "ehd.subBasinFallbackName": "Cuenca {n}",
  "ehd.zoneFallbackName": "Zona {n}",
  "ehd.snowZoneFallbackName": "Zona {n}-nieve",

  // --- examples.ts ---
  "examples.zoneFallbackName": "Zona {model}",
  "examples.fileLoadError": 'No se ha podido cargar "{url}" ({status}).',
  "examples.fileNotFoundError": 'No existe el fichero "{name}" en este ejemplo.',
  "examples.folderNotFoundError": 'No existe la carpeta "{name}" en este ejemplo.',

  // --- project.ts (openProject) ---
  "project.noEhdFoundError": "No se ha encontrado ningún fichero .ehd en la carpeta seleccionada.",
  "project.ehdTooLarge": '"{name}" pesa más de 100 MB — demasiado grande para abrirlo.',
  "project.scenarioFolderNotFoundWarning":
    'No se ha encontrado la carpeta de escenario "{folder}" (referenciada en el proyecto) dentro de la carpeta seleccionada.',
  "project.noScenarioPathWarning":
    'El proyecto no tiene un "RUTA_ESCENARIO" registrado; no se pueden localizar sus ficheros de entrada.',
  "project.fileNotDeclaredWarning": 'El proyecto no declara el fichero de "{label}".',
  "project.requiredSeriesNotReadWarning": 'No se ha podido leer "{file}" ({label}).',
  "project.optionalSeriesNotReadWarning":
    'No se ha podido leer "{file}" ({label}). Es opcional, se continúa sin ella.',

  // --- importCsvProject.ts (openCsvProject) ---
  "importCsvProject.cuencasNotFoundError":
    'No se ha encontrado ningún fichero .ehd ni "Ent_Cuencas.csv" en la carpeta seleccionada.',
  "importCsvProject.noConfigWarning":
    'No se ha encontrado "Ent_Config.csv" — se usan las unidades y opciones por defecto.',
  "importCsvProject.noPrecipitationWarning":
    'No se ha encontrado "Ent_Precip.csv" en la carpeta — la serie de precipitación no se ha cargado.',
  "importCsvProject.noEtpWarning":
    'No se ha encontrado "Ent_Etp.csv" en la carpeta — la serie de ETP no se ha cargado.',

  // --- updateProjectFromCsv.ts ---
  "updateCsvProject.dialogTitle": "Actualizar desde CSVs",
  "updateCsvProject.subBasinsUpdated": "{n} subcuenca(s) actualizada(s)",
  "updateCsvProject.zonesSummary": "{updated} zona(s) actualizada(s), {created} nueva(s)",
  "updateCsvProject.aquifersSummary": "{updated} acuífero(s) actualizado(s), {created} nuevo(s)",
  "updateCsvProject.outletsSummary": "{created} punto(s) de desagüe nuevo(s)",
  "updateCsvProject.configUpdated": "Unidades y opciones de acuíferos actualizadas",
  "updateCsvProject.apply": "Aplicar cambios",
  "updateCsvProject.duplicateZoneAcrossFiles":
    'La zona "{name}" aparece en más de un fichero de parámetros — se ha usado el último encontrado.',
  "updateCsvProject.subBasinNotFound":
    '"{name}" no coincide con ninguna subcuenca del proyecto abierto — esa fila se ha ignorado (esto nunca crea subcuencas nuevas).',
  "updateCsvProject.unresolvedReference":
    'La subcuenca "{subBasin}" hace referencia a un/a {kind} llamado/a "{name}" que no existe — se ha dejado sin asignar.',

  // --- projectEntities.ts (alta/edición de subcuencas, zonas, puntos de desagüe, acuíferos) ---
  "projectEntities.emptySubBasinName": "El nombre de la subcuenca no puede estar vacío.",
  "projectEntities.emptyCsv": "El archivo CSV está vacío.",
  "projectEntities.csvColumnsNotFound":
    "No se han encontrado las columnas de nombre y superficie — la primera fila debe ser la cabecera (por ejemplo: nombre;superficie).",
  "projectEntities.emptyZoneName": "El nombre de la zona no puede estar vacío.",
  "projectEntities.zoneAlreadyExists": 'Ya existe una zona "{name}" con ese mismo tipo de modelo.',
  "projectEntities.zoneDoesNotExist": "No existe esa zona.",
  "projectEntities.emptyOutletPointName": "El nombre del punto de desagüe no puede estar vacío.",
  "projectEntities.emptyAquiferName": "El nombre del acuífero no puede estar vacío.",
  "projectEntities.invalidVo": "Vo (volumen inicial del acuífero) debe ser mayor que 0.",
  "projectEntities.invalidAlfa": "Alfa (coeficiente de descarga) debe ser mayor que 0.",
  "projectEntities.defaultSubBasinName": "Subcuenca {n}",
  "projectEntities.defaultOutletPointName": "Desagüe {n}",
  "projectEntities.defaultAquiferName": "Acuífero {n}",
  "projectEntities.hbvModelNotFound": "No se ha encontrado el modelo HBV en el catálogo.",

  // --- gisUpload.ts (loadGisFiles) ---
  "gisUpload.noGeometries": "El fichero no tiene ninguna geometría.",
  "gisUpload.missingShp": "Falta el fichero .shp (o sube un .geojson).",
  "gisUpload.shapefileNoGeometries": "El shapefile no tiene ninguna geometría.",
  "gisUpload.missingDbf": "Falta el .dbf: no se podrán ver ni usar los atributos del shapefile.",
  "gisUpload.missingPrj": "Falta el .prj: se asume que las coordenadas ya están en WGS84.",
  "gisUpload.fileTooLarge": '"{name}" pesa más de 100 MB — demasiado grande para importarlo.',

  // --- gisLayer.ts (parseGeoJSON) ---
  "gisLayer.invalidJson": "El fichero no es JSON válido.",
  "gisLayer.notFeatureCollection": 'El fichero no es un GeoJSON de tipo "FeatureCollection".',

  // --- exportProject.ts (importProject) ---
  "exportProject.decompressError":
    '"{name}" no se ha podido descomprimir — ¿es un fichero .ehdz válido?',
  "exportProject.readInternalFormatError":
    '"{name}" no se ha podido leer (formato interno dañado).',
  "exportProject.notExportedError": '"{name}" no es una copia exportada de EvalHid.',
  "exportProject.newerVersionError":
    '"{name}" se exportó con una versión más nueva de la app — recarga la página e inténtalo de nuevo.',
  "exportProject.corruptedDataError":
    '"{name}" no tiene el contenido esperado de un proyecto de EvalHid (fichero dañado o incompleto).',

  // --- dailySeries.ts (parsePastedSeries / insertColumnInSeries) ---
  "dailySeries.emptyDateFields":
    'Fecha con campos vacíos (día="{day}", mes="{month}", año="{year}")',
  "dailySeries.noPastedData": "No se ha pegado ningún dato.",
  "dailySeries.lineInvalidFormat":
    'Línea {n}: se esperan dos columnas separadas por tabulador (fecha y valor) — "{line}".',
  "dailySeries.lineInvalidValue": 'Línea {n}: valor "{value}" no es un número.',
  "dailySeries.datesDoNotMatch":
    'Las fechas pegadas ({n} filas) no coinciden con las de la serie ya cargada ({total} filas, de {from} a {to}) — pega el mismo rango de fechas, o sustituye la serie entera desde "Series de entrada".',

  // --- seriesScale.ts ---
  "seriesScale.daily": "Diaria",
  "seriesScale.monthly": "Mensual",
  "seriesScale.dailyPasteFormat": "dd/mm/aaaa",
  "seriesScale.monthlyPasteFormat": "mm/aaaa",
  "seriesScale.monthlyFormatDescription":
    "mm/aaaa (también se acepta dd/mm/aaaa con el día fijo en 01)",
  "seriesScale.autoDetectSuffix":
    "La escala del proyecto aún no está fijada — se detecta automáticamente a partir de este primer archivo.",
  "seriesScale.dailyDateFormatMismatch":
    'Fecha "{date}" no reconocida — el proyecto está en escala diaria, se espera dd/mm/aaaa.',
  "seriesScale.monthlyDateFormatMismatch":
    'Fecha "{date}" no reconocida — el proyecto está en escala mensual, se espera mm/aaaa.',
  "seriesScale.dateFormatUnrecognized": 'Fecha "{date}" no reconocida.',
  "seriesScale.aemetDailyOnly":
    "Solo disponible para proyectos con escala diaria — AEMET/ROCIO no ofrecen series mensuales.",

  // --- genericLayer.ts / GenericLayersPanel.tsx ---
  "genericLayer.emptyName": "El nombre de la capa no puede estar vacío.",
  "genericLayer.unsupportedGeometry":
    "No se ha podido reconocer el tipo de geometría del archivo (se esperan puntos, líneas o polígonos).",
  "genericLayersPanel.intro":
    "Sube un archivo (shapefile o GeoJSON) para mostrarlo como capa de referencia en el mapa.",
  "genericLayersPanel.addButton": "Añadir capa",
  "genericLayersPanel.deleteDialogTitle": "Eliminar capa",
  "genericLayersPanel.deletePermanent": "Eliminar capa (permanente)",
  "genericLayersPanel.kind.point": "Punto",
  "genericLayersPanel.kind.line": "Línea",
  "genericLayersPanel.kind.polygon": "Polígono",
  "genericLayersPanel.deleteMessage":
    'Se eliminará "{name}" de forma permanente — no se puede deshacer. Puedes volver a subir el mismo archivo si la necesitas otra vez.',
  "genericLayersPanel.color": "Color",
  "genericLayersPanel.radius": "Radio",
  "genericLayersPanel.weight": "Grosor",
  "genericLayersPanel.fillOpacity": "Opacidad",
} as const;

export type TextKey = keyof typeof es;
